/**
 * HierarchicalThreatReview — Step 4's view when `analysis_mode ===
 * 'hierarchical'`. Two outer tabs (Current TARA / Child TARAs); the
 * STRIDE filter pill row appears inside each tab.
 *
 * Mirror of step3-scenarios/HierarchicalScenarioReview.tsx.
 */
import React, { useMemo, useState } from 'react';
import TableTabNav from '../../components/TableTabNav/TableTabNav';
import ThreatTable from './ThreatTable';
import ChildThreatsTable, { type ChildThreatView } from './ChildThreatsTable';
import type { ThreatRecord } from './threatFieldSchema';
import type { HierarchicalModifications } from '../stepBodyProps';

const TAB_CURRENT = 'current';
const TAB_CHILD = 'child';

export interface HierarchicalThreatReviewProps {
  newThreats: ThreatRecord[];
  childThreats: ChildThreatView[];
  editable: boolean;
  onAdd: () => void;
  onEdit: (threat: ThreatRecord) => void;
  onDelete: (threat: ThreatRecord) => void;
  /** Export + import slots — only wired into the Current TARA tab. */
  onExport?: () => void;
  onImport?: (file: File) => void;
  modifications: HierarchicalModifications;
  onToggleRelevance: (threatId: string, isRelevant: boolean) => void;
  onJustificationChange: (threatId: string, justification: string) => void;
  childInputsDisabled?: boolean;
  /** Persist child modifications on Done (Story 15.F). */
  onSaveChildModifications?: () => Promise<void> | void;
}

export default function HierarchicalThreatReview({
  newThreats,
  childThreats,
  editable,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  modifications,
  onToggleRelevance,
  onJustificationChange,
  childInputsDisabled,
  onSaveChildModifications,
}: HierarchicalThreatReviewProps) {
  const [activeTab, setActiveTab] = useState<string>(TAB_CURRENT);

  const childView: ChildThreatView[] = useMemo(
    () =>
      childThreats.map((c) => ({
        ...c,
        is_relevant: modifications[c.threat_id]?.is_relevant ?? c.is_relevant ?? true,
        relevance_justification:
          modifications[c.threat_id]?.relevance_justification ?? c.relevance_justification ?? '',
      })),
    [childThreats, modifications],
  );

  const handleToggle = (threatId: string) => {
    const current = childView.find((c) => c.threat_id === threatId);
    onToggleRelevance(threatId, !(current?.is_relevant !== false));
  };

  const tabs = [
    { key: TAB_CURRENT, label: `Current TARA (${newThreats.length})` },
    { key: TAB_CHILD, label: `Child TARAs (${childThreats.length})` },
  ];

  return (
    <>
      <TableTabNav
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Hierarchical threat review tabs"
        bleed={false}
        sticky={false}
      />

      {activeTab === TAB_CURRENT ? (
        <ThreatTable
          threats={newThreats}
          editable={editable}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onExport={onExport}
          onImport={onImport}
          emptyMessage="No new threats yet for this TARA."
        />
      ) : (
        <ChildThreatsTable
          threats={childView}
          onToggleRelevance={handleToggle}
          onJustificationChange={onJustificationChange}
          disabled={childInputsDisabled}
          onSave={onSaveChildModifications}
        />
      )}
    </>
  );
}
